import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  PageHeader,
  Spinner,
  EmptyState,
  Badge,
  Modal,
  Drawer,
  Field,
  Select,
  Tabs,
  ErrorBanner,
  formatDate,
  formatDateTime,
} from '../components/ui';

interface Contact {
  id: string;
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  type: 'lead' | 'client' | 'archived';
  tags?: string[];
  createdAt: string;
}

interface Tag {
  id: string;
  name: string;
  color?: string;
}

interface SmartList {
  id: string;
  name: string;
  filters: Record<string, unknown>;
}

interface ActivityItem {
  id: string;
  type: string;
  summary: string;
  occurredAt: string;
}

type DetailTab = 'overview' | 'activity' | 'notes' | 'tags';

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [smartListId, setSmartListId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showSmartList, setShowSmartList] = useState(false);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ['contacts', search, smartListId],
    queryFn: () => {
      const params: Record<string, string> = { limit: '50' };
      if (search) params.search = search;
      if (smartListId) params.smartListId = smartListId;
      return api.get<{ data: Contact[]; meta: { total: number } }>('/contacts', params);
    },
  });

  const tagsQ = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.get<{ data: Tag[] }>('/tags').catch(() => ({ data: [] })),
  });

  const smartListsQ = useQuery({
    queryKey: ['smart-lists'],
    queryFn: () => api.get<{ data: SmartList[] }>('/smart-lists').catch(() => ({ data: [] })),
  });

  const createMut = useMutation({
    mutationFn: (input: { firstName: string; lastName?: string; email?: string; phone?: string; type: 'lead' | 'client' }) =>
      api.post('/contacts', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      setShowCreate(false);
    },
  });

  const bulkTagMut = useMutation({
    mutationFn: (tagId: string) => api.post('/contacts/bulk/tags', { contactIds: Array.from(selectedIds), tagId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      setSelectedIds(new Set());
    },
  });

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const contacts = listQ.data?.data ?? [];
  const tags = tagsQ.data?.data ?? [];
  const smartLists = smartListsQ.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle={`${listQ.data?.meta.total ?? 0} total`}
        action={
          <>
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              Import / Export
            </Button>
            <Button onClick={() => setShowCreate(true)}>+ New contact</Button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 'var(--space-4)' }}>
        <aside>
          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>
            Smart lists
          </div>
          <button
            onClick={() => setSmartListId(null)}
            style={smartListBtn(smartListId === null)}
          >
            All
          </button>
          {smartLists.map((sl) => (
            <button key={sl.id} onClick={() => setSmartListId(sl.id)} style={smartListBtn(smartListId === sl.id)}>
              {sl.name}
            </button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setShowSmartList(true)} style={{ marginTop: 'var(--space-2)' }}>
            + Smart list
          </Button>

          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
            Tags
          </div>
          {tags.length === 0 ? (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', margin: 0 }}>No tags yet.</p>
          ) : (
            tags.map((t) => (
              <div key={t.id} style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}>
                <span style={{ background: t.color ?? 'var(--color-brand-primary)', color: 'white', padding: '2px 6px', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-xs)' }}>
                  {t.name}
                </span>
              </div>
            ))
          )}
        </aside>

        <div>
          <div style={{ marginBottom: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)' }}>
            <Input value={search} onChange={setSearch} placeholder="Search name, email, or phone…" style={{ flex: 1 }} />
          </div>

          {selectedIds.size > 0 && (
            <Card style={{ background: 'var(--color-brand-primary-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)' }}>{selectedIds.size} selected</span>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <select
                    onChange={(e) => e.target.value && bulkTagMut.mutate(e.target.value)}
                    defaultValue=""
                    style={{ padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--text-sm)' }}
                  >
                    <option value="">Apply tag…</option>
                    {tags.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                    Clear
                  </Button>
                </div>
              </div>
            </Card>
          )}

          <Card style={{ padding: 0 }}>
            {listQ.isLoading ? (
              <Spinner />
            ) : contacts.length === 0 ? (
              <EmptyState
                title={search ? 'No matches' : 'No contacts yet'}
                description={search ? 'Try a different search.' : 'Add your first contact.'}
                action={!search ? <Button onClick={() => setShowCreate(true)}>+ New contact</Button> : undefined}
              />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-surface-secondary)', textAlign: 'left', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                    <th style={{ ...cellStyle, width: 32 }}>
                      <input
                        type="checkbox"
                        checked={contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id))}
                        onChange={(e) => setSelectedIds(e.target.checked ? new Set(contacts.map((c) => c.id)) : new Set())}
                      />
                    </th>
                    <th style={cellStyle}>Name</th>
                    <th style={cellStyle}>Email</th>
                    <th style={cellStyle}>Phone</th>
                    <th style={cellStyle}>Type</th>
                    <th style={cellStyle}>Added</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={cellStyle} onClick={() => setSelected(c)}>
                        <strong>
                          {c.firstName} {c.lastName ?? ''}
                        </strong>
                      </td>
                      <td style={cellStyle} onClick={() => setSelected(c)}>{c.email ?? '—'}</td>
                      <td style={cellStyle} onClick={() => setSelected(c)}>{c.phone ?? '—'}</td>
                      <td style={cellStyle} onClick={() => setSelected(c)}>
                        <Badge color={c.type === 'client' ? 'green' : c.type === 'lead' ? 'blue' : 'gray'}>{c.type}</Badge>
                      </td>
                      <td style={cellStyle} onClick={() => setSelected(c)}>{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>

      {showCreate && (
        <CreateContactModal
          onClose={() => setShowCreate(false)}
          onSubmit={(input) => createMut.mutate(input)}
          loading={createMut.isPending}
          error={createMut.error instanceof Error ? createMut.error.message : null}
        />
      )}

      {selected && (
        <ContactDrawer
          contact={selected}
          tags={tags}
          onClose={() => setSelected(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['contacts'] })}
        />
      )}

      {showImport && <ImportExportModal onClose={() => setShowImport(false)} />}
      {showSmartList && (
        <CreateSmartListModal
          onClose={() => setShowSmartList(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['smart-lists'] });
            setShowSmartList(false);
          }}
        />
      )}
    </div>
  );
}

function CreateContactModal({
  onClose,
  onSubmit,
  loading,
  error,
}: {
  onClose: () => void;
  onSubmit: (input: { firstName: string; lastName?: string; email?: string; phone?: string; type: 'lead' | 'client' }) => void;
  loading: boolean;
  error: string | null;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [type, setType] = useState<'lead' | 'client'>('lead');

  return (
    <Modal onClose={onClose} title="New contact">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({
            firstName,
            lastName: lastName || undefined,
            email: email || undefined,
            phone: phone || undefined,
            type,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="First name *">
          <Input value={firstName} onChange={setFirstName} autoFocus />
        </Field>
        <Field label="Last name">
          <Input value={lastName} onChange={setLastName} />
        </Field>
        <Field label="Email">
          <Input value={email} onChange={setEmail} type="email" />
        </Field>
        <Field label="Phone">
          <Input value={phone} onChange={setPhone} type="tel" placeholder="+1 555 123 4567" />
        </Field>
        <Field label="Type">
          <Select
            value={type}
            onChange={setType}
            options={[
              { value: 'lead', label: 'Lead' },
              { value: 'client', label: 'Client' },
            ]}
          />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || !firstName}>
            {loading ? 'Saving…' : 'Save contact'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function ContactDrawer({
  contact,
  tags,
  onClose,
  onChanged,
}: {
  contact: Contact;
  tags: Tag[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const notesQ = useQuery({
    queryKey: ['notes', contact.id],
    queryFn: () => api.get<{ data: { id: string; content: string; createdAt: string }[] }>(`/contacts/${contact.id}/notes`),
  });

  const activityQ = useQuery({
    queryKey: ['activity', contact.id],
    queryFn: () => api.get<{ data: ActivityItem[] }>(`/contacts/${contact.id}/activity`).catch(() => ({ data: [] })),
  });

  const noteMut = useMutation({
    mutationFn: (content: string) => api.post(`/contacts/${contact.id}/notes`, { content }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes', contact.id] }),
  });

  const tagMut = useMutation({
    mutationFn: (tagId: string) => api.post(`/contacts/${contact.id}/tags`, { tagId }),
    onSuccess: onChanged,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/contacts/${contact.id}`),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const [noteDraft, setNoteDraft] = useState('');

  return (
    <Drawer onClose={onClose} title={`${contact.firstName} ${contact.lastName ?? ''}`} width={520}>
      <Tabs<DetailTab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: 'Overview' },
          { value: 'activity', label: 'Activity' },
          { value: 'notes', label: 'Notes', count: notesQ.data?.data.length },
          { value: 'tags', label: 'Tags' },
        ]}
      />

      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <Info label="Email" value={contact.email ?? '—'} />
          <Info label="Phone" value={contact.phone ?? '—'} />
          <Info label="Type" value={<Badge color={contact.type === 'client' ? 'green' : 'blue'}>{contact.type}</Badge>} />
          <Info label="Added" value={formatDateTime(contact.createdAt)} />
        </div>
      )}

      {tab === 'activity' && (
        <div>
          {activityQ.isLoading ? (
            <Spinner />
          ) : (activityQ.data?.data ?? []).length === 0 ? (
            <p style={{ color: 'var(--color-text-tertiary)' }}>No activity yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {activityQ.data!.data.map((a) => (
                <li key={a.id} style={{ padding: 'var(--space-2)', background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ fontSize: 'var(--text-sm)' }}>{a.summary}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    {a.type} · {formatDateTime(a.occurredAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === 'notes' && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
            {(notesQ.data?.data ?? []).map((n) => (
              <Card key={n.id} style={{ padding: 'var(--space-2)' }}>
                <div style={{ fontSize: 'var(--text-sm)' }}>{n.content}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>{formatDateTime(n.createdAt)}</div>
              </Card>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Input value={noteDraft} onChange={setNoteDraft} placeholder="Add a note…" style={{ flex: 1 }} />
            <Button
              onClick={() => {
                if (noteDraft) {
                  noteMut.mutate(noteDraft);
                  setNoteDraft('');
                }
              }}
              disabled={noteMut.isPending}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {tab === 'tags' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {tags.length === 0 ? (
            <p style={{ color: 'var(--color-text-tertiary)' }}>No tags exist yet. Create one in Settings.</p>
          ) : (
            tags.map((t) => (
              <button
                key={t.id}
                onClick={() => tagMut.mutate(t.id)}
                style={{
                  background: t.color ?? 'var(--color-brand-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '4px 10px',
                  borderRadius: 'var(--radius-full)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                }}
              >
                + {t.name}
              </button>
            ))
          )}
        </div>
      )}

      <ErrorBanner error={error} />
      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
          Delete
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
    </Drawer>
  );
}

function ImportExportModal({ onClose }: { onClose: () => void }) {
  const exportUrl = `${(import.meta.env.VITE_API_URL as string | undefined) ?? 'https://frontdesk-api.blancorunning.workers.dev'}/api/v1/contacts/export`;
  return (
    <Modal onClose={onClose} title="Import / Export">
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        Export contacts as CSV, or import a CSV (one row per contact: firstName, lastName, email, phone, type).
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <Button variant="secondary" onClick={() => window.open(exportUrl, '_blank')}>
          Export CSV
        </Button>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <input
            type="file"
            accept=".csv"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const text = await file.text();
              await api.post('/contacts/import', { csv: text }).catch(() => null);
              onClose();
            }}
          />
        </label>
      </div>
    </Modal>
  );
}

function CreateSmartListModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'lead' | 'client' | 'archived'>('lead');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => api.post('/smart-lists', { name, filters: { type } }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New smart list">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Filter by type">
          <Select
            value={type}
            onChange={setType}
            options={[
              { value: 'lead', label: 'Leads' },
              { value: 'client', label: 'Clients' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>{value}</div>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

function smartListBtn(active: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    padding: 'var(--space-1) var(--space-2)',
    border: 'none',
    background: active ? 'var(--color-brand-primary-light)' : 'transparent',
    color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: 'var(--text-sm)',
    borderRadius: 'var(--radius-md)',
    fontWeight: active ? 600 : 400,
  };
}
